import { DescriptionOutlined, EditNoteOutlined } from '@mui/icons-material';
import { Link, styled, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { selectFolders } from '../../../redux/reducers/folderReducer';
import { DealGPTView } from '../../../Types/enums';
import { SourceDocument } from '../../../Types/indexes';
import { AnalyticsEvent, useAnalytics } from '../../Providers/AnalyticsProvider';
import { useNavigationIndex } from '../../Providers/NavigationIndexProvider';

const SourceItem = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginTop: '4px',
  color: '#1976d2',
});

const SourceLink = styled(Link)({
  cursor: 'pointer',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitLineClamp: 1,
  fontWeight: '450',
  marginLeft: '8px',
  fontSize: '14px',
});

const SiteNotesText = styled(Typography)({
  color: '#1976d2',
  fontWeight: '500',
  marginLeft: '8px',
  fontSize: '14px',
});

const EditNoteIcon = styled(EditNoteOutlined)({
  color: '#070154',
  fontSize: '14px',
});

const DescriptionIcon = styled(DescriptionOutlined)({
  color: '#070154',
});

const getUniqueDocuments = (docs: SourceDocument[]) => {
  const uniqueDocs = docs.reduce((acc, current) => {
    const uniqueKey = current.openaiId || current.graphId;
    if (uniqueKey && !acc.some((item) => (item.openaiId || item.graphId) === uniqueKey)) {
      acc.push(current);
    }
    return acc;
  }, [] as SourceDocument[]);

  return uniqueDocs;
};

interface ReferenceDocumentsProps {
  questionId: string;
  documents?: SourceDocument[] | null;
  showAnalysisText?: boolean;
  showReferenceTitle?: boolean;
  showValidationTitle?: boolean;
  siteNotes?: string[];
  view?: DealGPTView;
  showAll?: boolean;
}

const ReferenceDocuments: React.FC<ReferenceDocumentsProps> = ({
  questionId,
  documents,
  showAnalysisText = false,
  showReferenceTitle = true,
  showValidationTitle = false,
  siteNotes = [],
  view = DealGPTView.ANALYSIS,
  showAll = false,
}) => {
  const analytics = useAnalytics();
  const navigate = useNavigate();
  const { projectId } = useParams();

  const { goToPage } = useNavigationIndex();
  const { currentFolder } = useSelector(selectFolders);
  const [showingAll, setShowingAll] = useState(showAll);
  const [passedDocuments, setPassedDocuments] = useState<SourceDocument[]>(
    getUniqueDocuments(documents || [])
  );
  const [docsToShow, setDocsToShow] = useState(
    passedDocuments
      ? passedDocuments?.length > 3
        ? passedDocuments.slice(0, 2)
        : passedDocuments
      : []
  );

  useEffect(() => {
    setPassedDocuments(getUniqueDocuments(documents || []));
  }, [documents]);

  useEffect(() => {
    if (!passedDocuments) {
      setDocsToShow([]);
    } else {
      if (showingAll) {
        setDocsToShow(passedDocuments);
      } else {
        setDocsToShow(passedDocuments?.length > 2 ? passedDocuments.slice(0, 2) : passedDocuments);
      }
    }
  }, [showingAll, passedDocuments]);

  const handleSiteVisitNotesClick = useCallback(() => {
    const analyticsEvent =
      view == DealGPTView.ANALYSIS
        ? AnalyticsEvent.WM_IA_A_SOURCE_CLICK
        : view == DealGPTView.DISCOVERY
        ? AnalyticsEvent.WM_IA_D_SOURCE_CLICK
        : AnalyticsEvent.WM_IA_CHAT_SOURCE_CLICK;
    analytics.event(
      analyticsEvent,
      {
        id: questionId,
        value: 'Site Visit Notes',
      },
      {
        projectId,
        iaView: view,
        ...(view === DealGPTView.CHAT
          ? {
              messageId: questionId,
            }
          : {
              questionId,
            }),
      }
    );
    goToPage(0);
  }, [analytics, goToPage, questionId, view, projectId]);

  const handleScopeAreaNotesClick = useCallback(
    (notes: string) => {
      if (currentFolder) {
        navigate(`/folder/${currentFolder.id}/analysis/${notes}`);
      }
    },
    [navigate, currentFolder]
  );

  return (
    <div>
      {showReferenceTitle && (
        <Typography sx={{ fontSize: '14px', fontWeight: '700', color: '#757575' }}>
          REFERENCE DOCUMENT(S)
        </Typography>
      )}
      {showValidationTitle && (
        <Typography sx={{ fontSize: '14px', fontWeight: '700', color: '#757575' }}>
          VALIDATION DOCUMENT(S)
        </Typography>
      )}
      {(showAnalysisText || siteNotes.length > 0) && (
        <>
          <SourceItem>
            <EditNoteIcon />
            {siteNotes.length > 0 ? (
              <SiteNotesText>Site Visit Notes</SiteNotesText>
            ) : (
              <SourceLink onClick={handleSiteVisitNotesClick}>Site Visit Notes</SourceLink>
            )}
          </SourceItem>
          {siteNotes.map((notes, index) => (
            <SourceItem key={notes + '-' + index}>
              <EditNoteIcon sx={{ marginLeft: '20px' }} />
              <SourceLink onClick={() => handleScopeAreaNotesClick(notes)}>{notes}</SourceLink>
            </SourceItem>
          ))}
        </>
      )}
      {docsToShow.map((document, index) => (
        <div key={document.fileName + '-' + index}>
          {document.fileName === 'Notes' ? (
            <SourceItem>
              <EditNoteIcon />
              <Tooltip
                placement="right"
                title={<div style={{ whiteSpace: 'pre-line' }}>{document.fileName}</div>}
              >
                <span>{document.fileName}</span>
              </Tooltip>
            </SourceItem>
          ) : (
            <SourceItem>
              <DescriptionIcon sx={{ fontSize: '14px' }} />
              <Tooltip
                placement="right"
                title={<div style={{ whiteSpace: 'pre-line' }}>{document.fileName}</div>}
              >
                <span>
                  <SourceLink
                    key={'notes'}
                    href={document.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      const analyticsEvent =
                        view == DealGPTView.ANALYSIS
                          ? AnalyticsEvent.WM_IA_A_SOURCE_CLICK
                          : AnalyticsEvent.WM_IA_D_SOURCE_CLICK;
                      analytics.event(
                        analyticsEvent,
                        {
                          id: questionId,
                          value: document.fileName,
                        },
                        {
                          projectId,
                          iaView: view,
                          referenceSelected: document.fileName,
                          referenceUrlSelected: document.link,
                          ...(view === DealGPTView.CHAT
                            ? {
                                messageId: questionId,
                              }
                            : {
                                questionId,
                              }),
                        }
                      );
                    }}
                  >
                    {document.fileName}
                  </SourceLink>
                </span>
              </Tooltip>
            </SourceItem>
          )}
        </div>
      ))}
      {passedDocuments && passedDocuments.length > 2 && (
        <Typography>
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              const analyticsEvent =
                view == DealGPTView.ANALYSIS
                  ? AnalyticsEvent.WM_IA_A_SHOW_ALL_SOURCES
                  : AnalyticsEvent.WM_IA_D_SHOW_ALL_SOURCES;
              analytics.event(
                analyticsEvent,
                {
                  id: questionId,
                  value: (!showingAll).toString(),
                },
                {
                  projectId,
                  iaView: view,
                  allCategoriesWereExpanded: showingAll,
                  allCategoriesNowExpanded: !showingAll,
                  ...(view === DealGPTView.CHAT
                    ? {
                        messageId: questionId,
                      }
                    : {
                        questionId,
                      }),
                }
              );
              setShowingAll(!showingAll);
            }}
            style={{
              color: '#757575',
              textDecoration: 'underline',
              verticalAlign: 'baseline',
              fontSize: '0.90rem',
              fontStyle: 'italic',
            }}
          >
            {showingAll ? 'Show Fewer' : 'Show All'}
          </a>
        </Typography>
      )}
    </div>
  );
};

export default ReferenceDocuments;
